import { createFileRoute } from "@tanstack/react-router";
import { Icon } from "@iconify/react";
import {
  Avatar,
  Button,
  Checkbox,
  CheckboxGroup,
  FieldError,
  Form,
  Input,
  Label,
  ListBox,
  Select,
  Switch,
  TextField,
  toast,
} from "@heroui/react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";

import {
  useLazyTariff,
  useTariffCategories,
  useUpdateTariff,
} from "@/features/tariffs/use-tariffs.ts";
import { usePackagesPrices } from "@/features/packages-price/use-packages-prices.ts";

export const Route = createFileRoute(
  "/dashboard/categories/$categoryId/tariffs/$tariffId/edit",
)({
  component: RouteComponent,
});

interface IFormInput {
  isActive: boolean;

  name: string;
  description: string;
  icon: string;
  image: string;
  sortIndex: number;
  tariffCategoryId: string;

  length: number;
  height: number;
  width: number;
  weight: number;

  costPriceOfAirShipment: number;
  packageId: string;
  additionalCostForFragileCargo: number;
}

function RouteComponent() {
  const { tariffId } = Route.useParams();
  const [fields, setFields] = useState<string[]>([]);
  const { data: tariff } = useLazyTariff({ tariffId });

  const { mutateAsync, isPending } = useUpdateTariff({
    onSuccess: async () => {
      toast.success("Успешно изменено");
    },
  });

  const packagesPrices = usePackagesPrices();
  const packages = packagesPrices.data.data.map((item) => ({
    key: `${item.id}`,
    title: item.name,
    description: item.description,
    image: item.image,
  }));

  const tariffCategories = useTariffCategories();
  const categories = tariffCategories.data.data.map((item) => ({
    key: `${item.id}`,
    title: item.name,
  }));

  const icons = [
    { key: "59717", icon: "solar:box-bold-duotone" },
    { key: "60762", icon: "solar:box-minimalistic-bold-duotone" },
  ];

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<IFormInput>({
    defaultValues: {
      packageId: tariff?.data.packageId?.toString(),
    },
  });

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    mutateAsync({
      id: Number(tariffId),
      isActive: data.isActive,
      name: data.name.trim(),
      description: data.description.trim(),
      icon: Number(data.icon),
      image: data.image,
      sortIndex: 0,
      fields,
      tariffCategoryId: Number(data.tariffCategoryId),

      length: Number(data.length),
      height: Number(data.height),
      width: Number(data.width),
      weight: Number(data.weight),

      costPriceOfAirShipment: Number(data.costPriceOfAirShipment),
      packageId: Number(data.packageId),
      additionalCostForFragileCargo: Number(data.additionalCostForFragileCargo),
    }).then(() => {
      reset();
    });
  };

  useEffect(() => {
    if (tariff?.data) {
      setFields(tariff.data.fields ?? []);
      setValue("packageId", tariff.data.packageId?.toString() ?? "");
      setValue(
        "tariffCategoryId",
        tariff.data.tariffCategoryId?.toString() ?? "",
      );
    }
  }, [tariff]);

  if (!tariff?.data) return null;

  return (
    <Form className="flex flex-col pt-4 pr-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-row gap-2 justify-between h-12 items-center w-full">
        <p className="font-bold text-xl">Изменить тариф</p>

        <Button isPending={isPending} type="submit" variant="primary">
          Сохранить
        </Button>
      </div>

      <div className="container mt-6 gap-4 flex flex-col max-w-[40rem] pb-24">
        <Controller
          control={control}
          defaultValue={tariff.data.isActive}
          name="isActive"
          render={({ field }) => (
            <Switch isSelected={field.value} onChange={field.onChange}>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Content>
                <Label>Активен</Label>
              </Switch.Content>
            </Switch>
          )}
        />

        <TextField
          defaultValue={tariff.data.name}
          isInvalid={!!errors.name?.message}
        >
          <Label>Название</Label>
          <Input {...register("name", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.name?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={tariff.data.description}
          isInvalid={!!errors.description?.message}
        >
          <Label>Описание</Label>
          <Input
            {...register("description", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.description?.message}</FieldError>
        </TextField>

        <Controller
          control={control}
          defaultValue={tariff.data.icon?.toString()}
          name="icon"
          render={({ field }) => (
            <Select
              isInvalid={!!errors.icon?.message}
              selectedKey={field.value ?? null}
              onSelectionChange={(key) => field.onChange(key as string)}
            >
              <Label>Иконка</Label>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <FieldError>{errors.icon?.message}</FieldError>
              <Select.Popover>
                <ListBox items={icons}>
                  {({ key, icon }) => (
                    <ListBox.Item id={key} textValue={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="size-5" icon={icon} />
                        <span>{key}</span>
                      </div>
                    </ListBox.Item>
                  )}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
          rules={{ required: "Нужно заполнить поле" }}
        />

        <TextField
          defaultValue={tariff.data.image}
          isInvalid={!!errors.image?.message}
        >
          <Label>Ссылка на картинку</Label>
          <Input {...register("image", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.image?.message}</FieldError>
        </TextField>

        <Controller
          control={control}
          defaultValue={tariff.data.tariffCategoryId?.toString()}
          name="tariffCategoryId"
          render={({ field }) => (
            <Select
              isInvalid={!!errors.tariffCategoryId?.message}
              selectedKey={field.value ?? null}
              onSelectionChange={(key) => field.onChange(key as string)}
            >
              <Label>Категория</Label>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <FieldError>{errors.tariffCategoryId?.message}</FieldError>
              <Select.Popover>
                <ListBox items={categories}>
                  {({ key, title }) => (
                    <ListBox.Item id={key} textValue={title}>
                      {title}
                    </ListBox.Item>
                  )}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
          rules={{ required: "Нужно заполнить поле" }}
        />

        <TextField
          defaultValue={tariff.data.length?.toString()}
          isInvalid={!!errors.length?.message}
        >
          <Label>Длина (в см.)</Label>
          <Input
            {...register("length", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.length?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={tariff.data.height?.toString()}
          isInvalid={!!errors.height?.message}
        >
          <Label>Высота (в см.)</Label>
          <Input
            {...register("height", { required: "Нужно заполнить поле" })}
          />
          <FieldError>{errors.height?.message}</FieldError>
        </TextField>

        <TextField
          defaultValue={tariff.data.width?.toString()}
          isInvalid={!!errors.width?.message}
        >
          <Label>Ширина (в см.)</Label>
          <Input {...register("width", { required: "Нужно заполнить поле" })} />
          <FieldError>{errors.width?.message}</FieldError>
        </TextField>

        <TextField defaultValue={tariff.data.weight?.toString()}>
          <Label>Реальный вес (в кг.)</Label>
          <Input {...register("weight")} />
        </TextField>

        <TextField
          defaultValue={tariff.data.costPriceOfAirShipment?.toString()}
        >
          <Label>Цена себестоимости авиа отправки за 1 кг</Label>
          <Input {...register("costPriceOfAirShipment")} />
        </TextField>

        <Controller
          control={control}
          defaultValue={tariff.data.packageId?.toString()}
          name="packageId"
          render={({ field }) => (
            <Select
              isInvalid={!!errors.packageId?.message}
              selectedKey={field.value ?? null}
              onSelectionChange={(key) => field.onChange(key as string)}
            >
              <Label>Упаковка товара в среднем за единицу</Label>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <FieldError>{errors.packageId?.message}</FieldError>
              <Select.Popover>
                <ListBox items={packages}>
                  {({ key, title, description, image }) => (
                    <ListBox.Item id={key} textValue={title}>
                      <div className="flex items-center gap-2">
                        <Avatar size="sm">
                          <Avatar.Image alt={title} src={image} />
                          <Avatar.Fallback>{title[0]}</Avatar.Fallback>
                        </Avatar>
                        <div>
                          <div>{title}</div>
                          <div className="text-xs text-neutral-500">
                            {description}
                          </div>
                        </div>
                      </div>
                    </ListBox.Item>
                  )}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
          rules={{ required: "Нужно заполнить поле" }}
        />

        <TextField
          defaultValue={tariff.data.additionalCostForFragileCargo?.toString()}
        >
          <Label>Доп расход на хрупкий груз</Label>
          <Input {...register("additionalCostForFragileCargo")} />
        </TextField>

        <div>
          <p className="text-sm font-medium mb-2">
            Поля доступные для заполнения
          </p>
          <CheckboxGroup value={fields} onChange={setFields}>
            <Checkbox value="WIDTH">
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Content>Ширина</Checkbox.Content>
            </Checkbox>
            <Checkbox value="LENGTH">
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Content>Длина</Checkbox.Content>
            </Checkbox>
            <Checkbox value="HEIGHT">
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Content>Высота</Checkbox.Content>
            </Checkbox>
            <Checkbox value="WEIGHT">
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Content>Вес</Checkbox.Content>
            </Checkbox>
            <Checkbox value="VOLUMETRIC_WEIGHT">
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Content>Объемный вес</Checkbox.Content>
            </Checkbox>
          </CheckboxGroup>
        </div>
      </div>
    </Form>
  );
}
